/**
 * Manga Source Context
 * Thin wrapper around MangaListContext (mirrors AnimeSourceContext pattern).
 * Provides a unified API for manga data access.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useMangaList } from './MangaListContext';
import { useAniList } from './AniListContext';
import { useAdultContent } from './AdultContentContext';
import { calculateMangaGroupStats } from '../services/groupingService';

const MangaSourceContext = createContext();

export const MangaSourceProvider = ({ children }) => {
    const { isConnected, username } = useAniList();
    const { hideAdultContent } = useAdultContent();
    const {
        mangaUserData,
        mangaGroups,
        pendingManga,
        latestChapterMap,
        pendingChapterMap,
        isLoading,
        isMangaReady,
        refreshMangaData,
        updateMangaDataDirect,
    } = useMangaList();

    // Recalculate group stats when latestChapterMap changes
    const enrichedMangaGroups = useMemo(() => {
        if (!mangaGroups || !latestChapterMap || Object.keys(latestChapterMap).length === 0) {
            return mangaGroups;
        }

        return mangaGroups.map(group => {
            const entries = group.anime || group.manga || [];
            const stats = calculateMangaGroupStats(entries, latestChapterMap);
            return { ...group, stats };
        });
    }, [mangaGroups, latestChapterMap]);

    // Filter out groups containing adult content when the setting is enabled
    const filteredMangaGroups = useMemo(() => {
        if (!hideAdultContent) return enrichedMangaGroups;
        if (!enrichedMangaGroups) return enrichedMangaGroups;
        return enrichedMangaGroups.filter(group => {
            const entries = group.anime || group.manga || [];
            return !entries.some(entry => entry.media?.isAdult);
        });
    }, [enrichedMangaGroups, hideAdultContent]);

    const value = {
        // Connection state (shared with anime)
        isConnected,
        username,
        // Manga data
        mangaUserData,
        mangaGroups: filteredMangaGroups,
        pendingManga,
        latestChapterMap,
        pendingChapterMap,
        // State
        isLoading,
        isMangaReady,
        // Actions
        refreshData: refreshMangaData,
        updateMangaDataDirect,
    };

    return (
        <MangaSourceContext.Provider value={value}>
            {children}
        </MangaSourceContext.Provider>
    );
};

export const useMangaSource = () => {
    const context = useContext(MangaSourceContext);
    if (!context) {
        throw new Error('useMangaSource must be used within a MangaSourceProvider');
    }
    return context;
};

export default MangaSourceContext;
